import {
  FieldError,
  FieldLabel,
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
  TextField,
} from '@serodigital/ui-components';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useHotWaterSchedules } from '../../contexts/hotWaterSchedules';
import { IZoneArray } from '../../helpers/scheduleHelper';
import { DeleteModal } from '../modal/deleteModal';
import { ScheduleDeleteButton } from './deleteButton';
import { hotWaterScheduleValidation } from './scheduleValidation';
import { Weekday, WeekdayCheckbox } from './weekdayCheckbox';

interface IScheduleModalProps {
  isOpen: boolean;
  title: string;
  zoneSchedules: IZoneArray;
  handleCloseModal: (message?: string) => void;
  selectedSchedule?: ISelectedSchedule;
}

export interface ISelectedSchedule {
  index: number;
  zoneId: string;
  start: string;
  day: string[];
}

export interface IHotWaterModalForm {
  startTime?: string;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
}

export const HotWaterScheduleModal = ({
  isOpen = false,
  title,
  zoneSchedules,
  handleCloseModal,
  selectedSchedule,
}: IScheduleModalProps) => {
  const { addMultipleSchedules, editMultipleSchedules } = useHotWaterSchedules();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const defaultValuesMemo = React.useMemo(() => {
    if (!selectedSchedule) return {};
    const startTime = selectedSchedule?.start;
    return {
      startTime,
      ...selectedSchedule?.day.reduce(
        (acc, day) => ({
          ...acc,
          [day]: true,
        }),
        {}
      ),
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, defaultValues },
  } = useForm<IHotWaterModalForm>({
    resolver: hotWaterScheduleValidation(zoneSchedules, selectedSchedule),
    defaultValues: defaultValuesMemo,
  });

  const onSubmit = async data => {
    setIsLoading(true);
    if (selectedSchedule) {
      /** Edit flow */
      const newZoneSchedules = cloneDeep(zoneSchedules);
      const newObj = {
        start: data.startTime,
        day: data.days,
      };
      const schedules = newZoneSchedules.hot_water;
      schedules[selectedSchedule.index] = { ...newObj };
      await editMultipleSchedules(newZoneSchedules);
    } else {
      await addMultipleSchedules(data);
    }

    setIsLoading(false);
    handleCloseModal();
  };

  async function onDelete() {
    const newZoneSchedules = cloneDeep(zoneSchedules);

    const schedules = newZoneSchedules.hot_water;
    schedules.splice(selectedSchedule.index, 1);
    await editMultipleSchedules(newZoneSchedules);
    handleCloseModal('Successfully deleted heating schedule');
  }

  const weekdayError = Boolean((errors as any)?.weekdays); // Fix typing issue

  return (
    <Modal data-qa="schedule-modal" isOpen={isOpen}>
      {isDeleting ? (
        <DeleteModal
          onCancel={() => setIsDeleting(false)}
          onConfirm={onDelete}
          title="Delete Hot Water Schedule?"
          body="Are you sure that you want to delete this schedule?"
          data-qa="schedule"
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalTitle headerClassName="!ml-0 !mb-8">{isDeleting ? 'Delete Schedule' : title}</ModalTitle>
          <ModalBody>
            <FieldLabel label="Schedule Days" />
            <div className="flex space-x-24">
              <WeekdayCheckbox
                defaultChecked={defaultValues?.monday}
                weekday={Weekday.MONDAY}
                register={register}
                name="monday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.tuesday}
                weekday={Weekday.TUESDAY}
                register={register}
                name="tuesday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.wednesday}
                weekday={Weekday.WEDNESDAY}
                register={register}
                name="wednesday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.thursday}
                weekday={Weekday.THURSDAY}
                register={register}
                name="thursday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.friday}
                weekday={Weekday.FRIDAY}
                register={register}
                name="friday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.saturday}
                weekday={Weekday.SATURDAY}
                register={register}
                name="saturday"
                error={weekdayError}
              />
              <WeekdayCheckbox
                defaultChecked={defaultValues?.sunday}
                weekday={Weekday.SUNDAY}
                register={register}
                name="sunday"
                error={weekdayError}
              />
            </div>
            <FieldError
              data-qa="weekday-error-field"
              error={weekdayError}
              errorMessage={(errors as any)?.weekdays as unknown as string} // Fix typing issue
            />
            <div className="flex space-x-24 mt-32">
              <TextField
                data-qa="schedule-start-time"
                label="Hot Water Ready For:"
                type={'time'}
                {...register('startTime')}
                error={Boolean(errors?.startTime)}
                errorMessage={errors?.startTime as any}
                step={900}
              />
            </div>
            <FieldError
              data-qa="form-zones-error-field"
              error={Boolean((errors as any)?.overlap)} // Fix typing issue
              errorMessage={(errors as any)?.overlap as unknown as string} // Fix typing issue
            />
            {selectedSchedule && <ScheduleDeleteButton onClick={() => setIsDeleting(true)} />}
          </ModalBody>
          <ModalFooter
            data-qa="schedule-submit"
            confirmButtonProps={{
              type: 'submit',
              className: '!bg-brand-primary !text-common-white',
              disabled: isLoading,
            }}
            confirmLabel={'Save'}
            onCancel={handleCloseModal}
            onConfirm={() => null}
          />
        </form>
      )}
    </Modal>
  );
};
